import { getVapidKey, savePushSubscription } from "./api";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const isIOS = () => {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export const isStandalonePWA = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export const pushSupported = () => {
  if (typeof window === "undefined") return false;
  // On iOS, Notification exists only when installed as PWA (iOS 16.4+)
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
};

export const pushPermission = () => {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission || "default";
};

// Register SW early on boot
export async function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  } catch (e) {
    console.warn("SW registration:", e);
    return null;
  }
}

// User-gesture push enablement
export async function enablePush() {
  if (!pushSupported()) {
    if (isIOS() && !isStandalonePWA()) {
      throw new Error("ios_not_installed");
    }
    throw new Error("unsupported");
  }

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("denied");

  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
  }
  await navigator.serviceWorker.ready;

  const key = await getVapidKey();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  if (sub) {
    await savePushSubscription(sub.toJSON());
  }
  return true;
}

// Auto-sync existing granted subscriptions on admin load
export async function syncPushIfGranted() {
  if (!pushSupported() || pushPermission() !== "granted") return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const key = await getVapidKey();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    if (sub) {
      await savePushSubscription(sub.toJSON());
      return true;
    }
  } catch (e) {
    console.warn("Push sync failed:", e);
  }
  return false;
}
